import {useMemo, useRef, useState} from 'react'
import {Formik} from 'formik'
import {useRouter} from 'next/router'
import Link from 'next/link'
import * as yup from 'yup'
import logger from '@/utils/logger'

import Field from './Field.js'
import {NOT_IN_US_LEAD_STATE} from '@/utils/usStates'
import {sortDegreesByName} from '@/utils/degrees'
import FieldGroup, {FieldRow} from './FieldGroup.js'
import SolidArrowsRotateIcon from '@/components/icons/solid/arrows-rotate'
import styles from './LeadForm.module.scss'
import Button from '@helios/Button'
import FormActions from '@helios/FormActions'
import Spinner from '@helios/Spinner'
import useCampus from '@/hooks/useCampus.ts'
import {usePostHog} from 'posthog-js/react'
import {usePardot} from './Pardot.js'
import states from '@/utils/states.json'
import {getApplicationMagicLink} from '@/utils/helpers.js'
import {
  captureAnalyticsEventOnce,
  captureLegacyAnalyticsEvent,
} from '@/lib/analytics'

export function getLeadSubmissionParams(campaignParams, source) {
  return {
    ...campaignParams,
    ...(source ? {utm_source: source} : {}),
  }
}

export function getEventInfoLeadFormOptions() {
  return {
    redirectPath: '/event-info/thank-you',
    shallowRedirect: true,
    source: 'event-info',
    useDegreesProxy: true,
  }
}

function LeadForm({
  site,
  onClose,
  redirectPath = '/request-info/thank-you',
  shallowRedirect = true,
  source,
  placeholders = {},
  labels = {},
  initialFieldOfInterest,
  useDegreesProxy = false,
}) {
  const isSacramento = site === 'sacramento'
  const location = isSacramento ? 'sacramento' : 'scholars'
  const router = useRouter()
  const {leadQuery, createLead, sendMagicLink, degreesQuery} = useCampus({
    location: location,
    enableLeadQuery: true,
    useDegreesProxy,
  })
  const campaignParams = usePardot()
  const posthog = usePostHog()
  const capturedBusinessEventsRef = useRef(new Set())
  const [resending, setResending] = useState(false)
  const lead = leadQuery.data
  // Alphabetical, like the application's program question.
  const degrees = useMemo(
    () => sortDegreesByName(degreesQuery.data || []),
    [degreesQuery.data],
  )
  const stateKeys = useMemo(() => {
    return Object.keys(states).sort((a, b) => 0)
  }, [])

  return (
    <Formik
      validateOnBlur={false}
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        state: '',
        fieldOfInterest:
          initialFieldOfInterest ??
          (isSacramento ? '' : 'Business Administration'),
      }}
      validationSchema={yup.object().shape({
        firstName: yup.string().required(),
        lastName: yup.string().required(),
        email: yup.string().email().required(),
        phone: yup.string().required().min(12),
        state: yup.string().required(),
        fieldOfInterest: yup.string().required(),
      })}
      onSubmit={async values => {
        const submissionParams = getLeadSubmissionParams(campaignParams, source)
        let redirectURI = getApplicationMagicLink(
          window.location.origin,
          submissionParams,
        )
        return createLead({
          data: {...values, redirectURI},
          campaignParams: submissionParams,
        })
          .then(createdLead => {
            const capturedContact = captureAnalyticsEventOnce(
              posthog,
              capturedBusinessEventsRef.current,
              `admissions_contact_submitted:${createdLead?.id || 'current'}`,
              'admissions_contact_submitted',
              {
                contact_type: 'request_info',
                site,
                source,
              },
            )
            if (shallowRedirect) {
              router.replace(
                {
                  pathname: router.pathname,
                  query: {site: router.query.site, requestInfo: 'true'},
                },
                redirectPath,
                {shallow: true},
              )
            } else {
              router.push(redirectPath)
            }
            if (process.env.NODE_ENV === 'production') {
              // push below fields to GTM datalayer; GTM's snippet defines it
              // only where its id is set
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Lead form submitted',
                email: createdLead.email,
                mobilePhone: createdLead.phone,
                firstName: createdLead.firstName,
                lastName: createdLead.lastName,
                state: createdLead.state,
                source,
              })
              if (capturedContact) {
                captureLegacyAnalyticsEvent(posthog, 'Lead Form Submitted', {
                  site,
                  location,
                  source,
                  email: createdLead.email,
                  phone: createdLead.phone,
                  firstName: createdLead.firstName,
                  lastName: createdLead.lastName,
                  state: createdLead.state,
                })
              }
            }
          })
          .catch(err => {
            logger.error(err, {
              description: 'Lead form submit failed',
              source: 'LeadForm',
            })
          })
      }}
    >
      {formik => {
        return router.asPath === redirectPath ? (
          <div className={styles['confirmation']}>
            <h1 className={styles['confirmation__title']}>
              We&apos;ll be in touch shortly.
            </h1>
            <>
              <p className={styles['confirmation__text']}>
                In the meantime, verify your email at{' '}
                <strong>{lead?.email}</strong> and get started on your
                application.
              </p>
              <p className={styles['confirmation__help']}>
                Didn&apos;t receive the email?
              </p>
              <Button
                shape="rounded"
                className={styles['confirmation__resend']}
                disabled={resending}
                onClick={async () => {
                  const resendCampaignParams = getLeadSubmissionParams(
                    campaignParams,
                    source,
                  )
                  let redirectURI = getApplicationMagicLink(
                    window.location.origin,
                    resendCampaignParams,
                  )
                  setResending(true)
                  await sendMagicLink(
                    {
                      email: lead?.email,
                      redirectURI,
                    },
                    resendCampaignParams,
                  ).catch(err =>
                    logger.error(err, {
                      description: 'Failed to resend magic link',
                      source: 'LeadForm',
                    }),
                  )
                  setTimeout(() => setResending(false), 1000)
                }}
                iconRight={<SolidArrowsRotateIcon />}
              >
                Resend the email
              </Button>
              <div className={styles['confirmation__change']}>
                or
                <Button
                  shape="rounded"
                  variant="text"
                  tone="brand"
                  onClick={() => {
                    router.push(
                      '/admissions-application?continue=true',
                      '/admissions-application',
                    )
                  }}
                >
                  change email address
                </Button>
              </div>
            </>
          </div>
        ) : (
          <>
            <form onSubmit={formik.handleSubmit}>
              <FieldRow>
                <FieldGroup label={labels.firstName || 'First Name'}>
                  <Field
                    name="firstName"
                    placeholder={placeholders.firstName}
                    autoFocus
                  />
                </FieldGroup>
                <FieldGroup label={labels.lastName || 'Last Name'}>
                  <Field name="lastName" placeholder={placeholders.lastName} />
                </FieldGroup>
                <FieldGroup label={labels.email || 'Email Address'}>
                  <Field
                    type="email"
                    name="email"
                    placeholder={placeholders.email}
                  />
                </FieldGroup>
                <FieldGroup label={labels.phone || 'Mobile Phone'}>
                  <Field
                    name="phone"
                    placeholder={placeholders.phone}
                    formatOptions={{phone: true, phoneRegionCode: 'US'}}
                  />
                </FieldGroup>
              </FieldRow>
              <FieldGroup
                label={
                  labels.state || 'Which state do you currently reside in?'
                }
              >
                <Field
                  as="select"
                  name="state"
                  emptyOptionLabel={placeholders.state}
                >
                  {stateKeys.map(k => (
                    <option key={k} value={k}>
                      {states[k]}
                    </option>
                  ))}
                  <option key={'not in US'} value={NOT_IN_US_LEAD_STATE}>
                    I don&apos;t live in the US
                  </option>
                </Field>
              </FieldGroup>
              <FieldGroup label={labels.fieldOfInterest || 'Field of Interest'}>
                {/* TODO this should be moved to use the degree information
                    we'll need to coordinate with Marketing */}
                <Field
                  as="select"
                  name="fieldOfInterest"
                  emptyOptionLabel={placeholders.fieldOfInterest}
                >
                  {degrees.map(degree => (
                    <option key={degree.id} value={degree.name}>
                      {degree.name}
                    </option>
                  ))}
                </Field>
              </FieldGroup>
              <div className={styles['consent']}>
                By entering your contact information and clicking “Submit,” you
                give consent to Campus to contact you by email, phone, or text
                message with information about Campus programs as described in
                the{' '}
                <Link href="/terms-of-use" target="_blank">
                  Terms and Conditions
                </Link>{' '}
                and{' '}
                <Link href="/privacy-policy" target="_blank">
                  Privacy Policy
                </Link>
                .
              </div>
              <FormActions>
                <Button
                  type="submit"
                  shape="rounded"
                  fullWidth
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? (
                    <Spinner label="Submitting" />
                  ) : (
                    'Submit'
                  )}
                </Button>
              </FormActions>
            </form>
          </>
        )
      }}
    </Formik>
  )
}

export default LeadForm
